/**
 * Queue Status Badge - Display status with appropriate color
 */
import type { QueueStatus } from '@/types';

interface QueueStatusBadgeProps {
  status: QueueStatus;
  size?: 'sm' | 'md' | 'lg';
}

export function QueueStatusBadge({ status, size = 'md' }: QueueStatusBadgeProps) {
  const getStatusConfig = (status: QueueStatus) => {
    switch (status) {
      case 'waiting':
        return {
          label: 'Waiting',
          icon: '⏳',
          className: 'bg-accent/10 text-accent border-accent/30',
        };
      case 'notified':
        return {
          label: 'Notified',
          icon: '🔔',
          className: 'bg-info/10 text-info border-info/30',
        };
      case 'arrived':
        return {
          label: 'Arrived',
          icon: '✅',
          className: 'bg-success/10 text-success border-success/30',
        };
      case 'in_service':
        return {
          label: 'In Service',
          icon: '✂️',
          className: 'bg-accent/10 text-accent border-accent/30',
        };
      case 'completed':
        return {
          label: 'Completed',
          icon: '🎉',
          className: 'bg-surface-2 text-ink border-hairline',
        };
      case 'cancelled':
        return {
          label: 'Cancelled',
          icon: '❌',
          className: 'bg-danger/10 text-danger border-danger/30',
        };
      case 'expired':
        return {
          label: 'Expired',
          icon: '⏰',
          className: 'bg-warning/10 text-warning border-warning/30',
        };
      default:
        return {
          label: status,
          icon: '❓',
          className: 'bg-surface-2 text-ink border-hairline',
        };
    }
  };

  const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
    switch (size) {
      case 'sm':
        return 'text-xs px-2 py-0.5';
      case 'md':
        return 'text-sm px-3 py-1';
      case 'lg':
        return 'text-base px-4 py-1.5';
      default:
        return 'text-sm px-3 py-1';
    }
  };

  const config = getStatusConfig(status);
  const sizeClasses = getSizeClasses(size);

  return (
    <span
      className={`inline-flex items-center gap-1 font-medium rounded-full border ${config.className} ${sizeClasses}`}
    >
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </span>
  );
}
